import { useState } from 'react';

import type { DateRange, AllDateRange } from '@hotjar/webapp/constants/DateFilterRanges';
import { DATE_RANGE_TYPE } from '@hotjar/webapp/constants/DateFilterRanges';
import type { HighlightType } from '@hotjar/webapp/domains/highlights/model';

import { useNavigate } from 'react-router-dom';
import { useQuery } from '@hotjar/webapp/utils/routing/utils/get-route';
import { useUpdateEffect } from 'react-use';
import { isEqual } from 'lodash-es';
import type { User as Author } from './AuthorFilter';
import {
  parseWithCustomParserAndDefaultValue,
  isValidAuthors,
  isValidDateRange,
  isValidLabels,
  isValidSources,
  isAbsoluteDateType,
  isValidStatus,
} from './utils/query-filter-validators';

export const SOURCES_CONFIG: Array<{
  value: HighlightType;
  labelKey: string;
  icon: string;
}> = [
  {
    value: 'heatmap',
    labelKey: 'highlights:filters.sources.heatmaps',
    icon: 'heatmaps',
  },
  {
    value: 'recording',
    labelKey: 'highlights:filters.sources.recordings',
    icon: 'recordings',
  },
];

enum Filters {
  AUTHORS = 'AUTHORS',
  DATE_RANGE = 'DATE_RANGE',
  LABELS = 'LABELS',
  SOURCES = 'SOURCES',
  STATUS = 'STATUS',
}

type LabelId = string;

type SourcesValues = (typeof SOURCES_CONFIG)[number]['value'];

type SolvedStatusType = 'solved' | 'unsolved' | 'both';

export const SOLVED_STATUS_CONFIG: Array<{
  value: SolvedStatusType;
  labelKey: string;
  icon: string;
}> = [
  {
    value: 'solved',
    labelKey: 'highlights:filters.status.solved',
    icon: 'check_alt',
  },
  {
    value: 'unsolved',
    labelKey: 'highlights:filters.status.unsolved',
    icon: 'check',
  },
];

export interface FiltersState {
  authors: Author[];
  dateRange: DateRange;
  labels: Set<LabelId>;
  sources: SourcesValues[];
  status: SolvedStatusType;
}

const initialState: FiltersState = {
  authors: [],
  dateRange: { type: DATE_RANGE_TYPE.ALL } as AllDateRange,
  labels: new Set<LabelId>(),
  sources: [],
  status: 'both',
};

export const useFilterState = (
  onChange: (filters: FiltersState) => void,
): [
  FiltersState,
  {
    onAuthorsChange: (users: Author[]) => void;
    onDateRangeChange: (date: DateRange) => void;
    onLabelsChange: (source: Set<LabelId>) => void;
    onSourcesChange: (source: SourcesValues[]) => void;
    onStatusChange: (source: SolvedStatusType) => void;
  },
] => {
  const navigate = useNavigate();
  const queryParams = useQuery();

  const getQueries = (): FiltersState => {
    const state = { ...initialState };

    state.authors = parseWithCustomParserAndDefaultValue(
      queryParams.get(Filters.AUTHORS),
      [],
      (value: unknown): Author[] => {
        if (!isValidAuthors(value)) {
          throw new Error('Invalid author filter parameter');
        }

        return value;
      },
    );

    state.dateRange = parseWithCustomParserAndDefaultValue(
      queryParams.get(Filters.DATE_RANGE),
      { type: DATE_RANGE_TYPE.ALL },
      (value: unknown): DateRange => {
        if (!isValidDateRange(value)) {
          throw new Error('Invalid data range filter parameter');
        }

        if (isAbsoluteDateType(value)) {
          return {
            type: DATE_RANGE_TYPE.ABSOLUTE,
            from: new Date(value.from),
            to: new Date(value.to),
          };
        }

        return value as DateRange;
      },
    );

    state.labels = parseWithCustomParserAndDefaultValue(
      queryParams.get(Filters.LABELS),
      new Set([]),
      (value: unknown): Set<LabelId> => {
        if (!isValidLabels(value)) {
          throw new Error('Invalid labels filter parameter');
        }

        return new Set(value);
      },
    );

    state.sources = parseWithCustomParserAndDefaultValue(
      queryParams.get(Filters.SOURCES),
      [],
      (value: unknown): SourcesValues[] => {
        if (!isValidSources(value)) {
          throw new Error('Invalid sources parameter');
        }

        return value;
      },
    );

    if (!isValidStatus(queryParams.get(Filters.STATUS))) {
      throw new Error('Invalid status parameter');
    }

    state.status = (queryParams.get(Filters.STATUS) ?? 'both') as SolvedStatusType;

    return state;
  };

  const [previousFilters, setPreviousFilters] = useState(getQueries());

  const getFilterState = (): FiltersState => {
    const state = getQueries();

    // if the new state deeply equals the initial state, return the initial to avoid useEffect trigger
    if (isEqual(state, previousFilters)) {
      return previousFilters;
    }

    setPreviousFilters(state);
    return state;
  };

  const filterState = getFilterState();

  useUpdateEffect(() => onChange(filterState), [onChange, filterState]);

  const onAuthorsChange = (users: Author[] = []) => {
    if (users.length > 0) {
      queryParams.set(Filters.AUTHORS, JSON.stringify(users));
    } else {
      queryParams.delete(Filters.AUTHORS);
    }

    navigate(`.?${new URLSearchParams(queryParams).toString()}`);
  };

  const onDateRangeChange = (dropdownSelection: DateRange) => {
    if (dropdownSelection.type === DATE_RANGE_TYPE.ALL) {
      queryParams.delete(Filters.DATE_RANGE);
    } else {
      queryParams.set(Filters.DATE_RANGE, JSON.stringify(dropdownSelection));
    }
    navigate(`.?${new URLSearchParams(queryParams).toString()}`);
  };

  const onLabelsChange = (labels: Set<string>) => {
    if (labels.size === 0) {
      queryParams.delete(Filters.LABELS);
    } else {
      queryParams.set(Filters.LABELS, JSON.stringify([...labels.values()]));
    }
    navigate(`.?${new URLSearchParams(queryParams).toString()}`);
  };

  const onSourcesChange = (sources: SourcesValues[]) => {
    if (sources.length === 0) {
      queryParams.delete(Filters.SOURCES);
    } else {
      queryParams.set(Filters.SOURCES, JSON.stringify(sources));
    }
    navigate(`.?${new URLSearchParams(queryParams).toString()}`);
  };

  const onStatusChange = (value: SolvedStatusType) => {
    queryParams.set(Filters.STATUS, value);
    navigate(`.?${new URLSearchParams(queryParams).toString()}`);
  };

  return [
    filterState,
    {
      onAuthorsChange,
      onDateRangeChange,
      onLabelsChange,
      onSourcesChange,
      onStatusChange,
    },
  ];
};
